import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import useAppSettingsStore from "@/store/appSettingsStore";
import { toast } from "react-toastify";
import packageJson from "../../package.json";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function UserIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );
}

function LockIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
  );
}

function PhoneIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
  );
}

function EyeIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );
}

function EyeOffIcon(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
      <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
  );
}

export default function Login() {
  const { login, mobileLogin, verifyLogin, verifyRegister, mobileRegister } = useAuth();
  const navigate = useNavigate();
  const { settings, fetchSettings } = useAppSettingsStore();

  // Username/Password login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Mobile/OTP login state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [verificationToken, setVerificationToken] = useState(null);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [timer, setTimer] = useState(0);
  const [lastSubmittedCode, setLastSubmittedCode] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loginMode, setLoginMode] = useState("username"); // "username" or "mobile"

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Timer effect for OTP
  useEffect(() => {
    if (verificationToken && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [verificationToken, timer]);

  // Reset to previous step when timer expires
  useEffect(() => {
    if (verificationToken && timer === 0 && !submitting) {
      toast.error("زمان تایید کد به پایان رسید. لطفا دوباره کد را دریافت کنید.");
      setVerificationToken(null);
      setOtpCode("");
      setError("");
      setTimer(0);
      setLastSubmittedCode("");
    }
  }, [timer, verificationToken, submitting]);

  // Auto-submit when 4 digits are entered
  useEffect(() => {
    if (otpCode.length === 4 && verificationToken && !submitting && otpCode !== lastSubmittedCode) {
      const submitOTP = async () => {
        if (!verificationToken) return;

        setLastSubmittedCode(otpCode);
        setSubmitting(true);
        setError("");
        try {
          // Use verifyRegister for registration, verifyLogin for login
          if (isRegisterMode) {
            await verifyRegister({ token: verificationToken, code: otpCode });
          } else {
            await verifyLogin({ token: verificationToken, code: otpCode });
          }
          navigate("/", { replace: true });
        } catch (err) {
          const errorMessage = err.message || "کد تایید نامعتبر است";
          toast.error(errorMessage);
          setError(errorMessage);
          // Don't reset lastSubmittedCode on error - user must change code to retry
        } finally {
          setSubmitting(false);
        }
      };
      submitOTP();
    }
  }, [otpCode, verificationToken, submitting, lastSubmittedCode, isRegisterMode, verifyLogin, verifyRegister, navigate]);

  // Username/Password login
  const handleUsernameLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login({ username, password });
      navigate("/", { replace: true });
    } catch (err) {
      const errorMessage = err.message || "ورود ناموفق بود. نام کاربری یا رمز اشتباه است.";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Mobile login/register - step 1: send OTP
  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = isRegisterMode
        ? await mobileRegister({ phoneNumber })
        : await mobileLogin({ phoneNumber });

      // Store token for verification step
      if (response.verificationToken) {
        setVerificationToken(response.verificationToken);
        setTimer(120); // 2 minutes timer
        toast.success("کد تایید به شماره شما ارسال شد");
      } else {
        throw new Error("توکن دریافت نشد");
      }
    } catch (err) {
      const errorMessage = err.message || "ارسال کد تایید ناموفق بود";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Mobile login - step 2: verify OTP
  const handleVerifyOTP = async (e) => {
    if (e) e.preventDefault();
    if (!verificationToken) {
      setError("لطفا ابتدا کد تایید را دریافت کنید");
      return;
    }

    if (otpCode.length !== 4) {
      return;
    }

    if (otpCode === lastSubmittedCode && submitting) {
      return; // Prevent duplicate submission
    }

    setLastSubmittedCode(otpCode);
    setSubmitting(true);
    setError("");
    try {
      // Use verifyRegister for registration, verifyLogin for login
      if (isRegisterMode) {
        await verifyRegister({ token: verificationToken, code: otpCode });
      } else {
        await verifyLogin({ token: verificationToken, code: otpCode });
      }
      navigate("/", { replace: true });
    } catch (err) {
      const errorMessage = err.message || "کد تایید نامعتبر است";
      toast.error(errorMessage);
      setError(errorMessage);
      // Don't reset lastSubmittedCode - user must change code to retry
    } finally {
      setSubmitting(false);
    }
  };

  const resetMobileFlow = () => {
    setVerificationToken(null);
    setOtpCode("");
    setError("");
    setTimer(0);
    setLastSubmittedCode("");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="relative flex items-center justify-center w-full min-h-[400px]">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-visible">
        {/* Logo */}
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-10">
          <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-lg border border-gray-100">
            {settings.app_logo ? (
              <img
                src={settings.app_logo}
                alt={settings.app_name || 'Logo'}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <UserIcon className="w-12 h-12" />
              </div>
            )}
          </div>
        </div>

        <div className="p-4 md:p-6 mt-10">
          {settings.app_name && (
            <div className="flex flex-col items-center mb-3">
              <h2 className="text-blue-600 text-base font-semibold text-center">
                {settings.app_name}
              </h2>
            </div>
          )}

          {/* <Tabs value={loginMode} onValueChange={setLoginMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="username">نام کاربری</TabsTrigger>
              <TabsTrigger value="mobile">شماره موبایل</TabsTrigger>
            </TabsList>

            <TabsContent value="username"> */}
          <form onSubmit={handleUsernameLogin} className="space-y-3">
            <div className="space-y-1.5">
              <label className="block text-sm text-muted-foreground text-right">نام کاربری</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80">
                  <UserIcon className="w-5 h-5" />
                </span>
                <Input
                  className="pl-10 text-right"
                  placeholder="نام کاربری"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm text-muted-foreground text-right">رمز عبور</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80 hover:text-muted-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
                <Input
                  className="pl-10 text-right"
                  placeholder="رمز عبور"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && <div className="text-destructive text-sm text-center">{error}</div>}

            <Button size="lg" className="w-full" type="submit" disabled={submitting}>
              {submitting ? "در حال ورود..." : "ورود"}
            </Button>
          </form>
          {/* </TabsContent>

            <TabsContent value="mobile">
              {!verificationToken ? (
                <>
                  <div className="flex gap-2 mb-3">
                    <Button
                      type="button"
                      variant={!isRegisterMode ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => {
                        setIsRegisterMode(false);
                        resetMobileFlow();
                      }}
                    >
                      ورود
                    </Button>
                    <Button
                      type="button"
                      variant={isRegisterMode ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => {
                        setIsRegisterMode(true);
                        resetMobileFlow();
                      }}
                    >
                      ثبت نام
                    </Button>
                  </div>

                  <form onSubmit={handleMobileSubmit} className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="block text-sm text-muted-foreground text-right">شماره موبایل</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80">
                          <PhoneIcon className="w-5 h-5" />
                        </span>
                        <Input
                          className="pl-10 text-right"
                          placeholder="09123456789"
                          inputMode="tel"
                          required
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                        />
                      </div>
                    </div>

                    {error && <div className="text-destructive text-sm text-center">{error}</div>}

                    <Button size="lg" className="w-full" type="submit" disabled={submitting}>
                      {submitting ? "در حال ارسال..." : isRegisterMode ? "ثبت نام و ارسال کد" : "ارسال کد تایید"}
                    </Button>
                  </form>
                </>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-3">
                  <div className="text-sm text-muted-foreground text-center mb-3">
                    <div>
                      کد تایید به شماره <span className="font-medium">{phoneNumber}</span> ارسال شد
                    </div>
                    <button
                      type="button"
                      onClick={resetMobileFlow}
                      className="text-xs text-blue-600 hover:underline mt-1 transition-colors"
                      disabled={submitting}
                    >
                      ویرایش شماره
                    </button>
                    {timer > 0 && (
                      <div className="mt-1 text-xs font-medium">
                        زمان باقی‌مانده: {formatTime(timer)}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-sm text-muted-foreground text-right">کد تایید</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/80">
                        <LockIcon className="w-5 h-5" />
                      </span>
                      <Input
                        className="pl-10 text-center text-lg tracking-widest"
                        placeholder="----"
                        inputMode="numeric"
                        maxLength={4}
                        required
                        value={otpCode}
                        onChange={(e) => {
                          const newCode = e.target.value.replace(/\D/g, "");
                          setOtpCode(newCode);
                          // Reset lastSubmittedCode when user changes the code
                          if (newCode !== lastSubmittedCode) {
                            setLastSubmittedCode("");
                          }
                        }}
                      />
                    </div>
                  </div>

                  {error && <div className="text-destructive text-sm text-center">{error}</div>}

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={resetMobileFlow}
                      disabled={submitting}
                    >
                      بازگشت
                    </Button>
                    <Button size="lg" className="flex-1" type="submit" disabled={submitting}>
                      {submitting ? "در حال تایید..." : "تایید"}
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>
          </Tabs> */}

          <div className="mt-4 pt-3 border-t border-gray-200 text-center text-xs text-gray-600">
            <div>
              طراحی شده توسط{" "}
              <a
                href="https://bazartalairan.ir/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline transition-colors"
              >
                بازار طلای ایران
              </a>
            </div>
            <div className="mt-1 opacity-70">
              نسخه {packageJson.version}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
